import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLoaderData, useOutletContext } from "react-router-dom";
import { isArray, debounce } from "lodash";
import { getEntities } from "../API";
import FilterDropdown from "./FilterDropdown";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function getSortValue(entity, field) {
  if (field.isObject) return entity[field.propertyName]?.name ?? "";
  if (isArray(field.propertyName)) return field.propertyName.map((p) => entity[p]).join(" ");
  return entity[field.propertyName] ?? "";
}

function Entities({ shema, extraFilters }) {
  const serverResponse = useLoaderData();

  const entities = normalizeResponse(serverResponse);
  const [filteredEntities, setFilteredEntities] = useState(entities);
  const [totalPages, setTotalPages] = useState(serverResponse?.totalPages ?? null);
  const [currentPage, setCurrentPage] = useState(serverResponse?.number ?? 0);
  const [sortConfig, setSortConfig] = useState({ field: null, dir: "asc" });

  useEffect(() => {
    setFilteredEntities(normalizeResponse(serverResponse));
    setTotalPages(serverResponse?.totalPages ?? null);
    setCurrentPage(serverResponse?.number ?? 0);
  }, [serverResponse]);

  const [searchText, setSearchText] = useOutletContext();
  const pageSize = 500;

  const filterFields = shema.fields.filter((f) => f.filter);
  const initialSelections = Object.fromEntries(filterFields.map((f) => [f.propertyName, ""]));
  const [filterSelections, setFilterSelections] = useState(initialSelections);
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    filterFields.forEach((field) => {
      if (field.filterOptions) return;
      getEntities(field.filterRoute, "?size=5000").then((response) => {
        const sorted = normalizeResponse(response).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
        setFilterOptions((prev) => ({ ...prev, [field.propertyName]: sorted }));
      });
    });
    if (searchText) search(searchText, 0, initialSelections);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();

  const userClick = (id) => navigate(`${id}`);

  const debounceSearch = useCallback(debounce((term, filters, size) => search(term, 0, filters, size), 500), []);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    debounceSearch(e.target.value, filterSelections, pageSize);
  };

  const handleFilter = (propertyName, value) => {
    const newSelections = { ...filterSelections, [propertyName]: value };
    setFilterSelections(newSelections);
    search(searchText, 0, newSelections, pageSize);
  };

const handleSort = (field) => {
    if (field.isArray || field.renderCell) return;
    setSortConfig((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );
  };

  const sortedEntities = useMemo(() => {
    if (sortConfig.field === null) return filteredEntities;
    return [...filteredEntities].sort((a, b) => {
      const av = getSortValue(a, sortConfig.field);
      const bv = getSortValue(b, sortConfig.field);
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
  }, [filteredEntities, sortConfig]);

  function buildTextPart(searchTerm) {
    if (!searchTerm) return "";
    const searchFields = shema.fields.filter((f) => f.search);
    // Multiple search fields should match if ANY of them contains the term, so they're
    // joined with "~" (OR) rather than "," (AND) in the search DSL.
    return searchFields.map((field) => field.propertyName + ":" + searchTerm).join("~");
  }

  function buildFilterPart(filters) {
    const active = Object.entries(filters ?? {}).filter(([, v]) => v !== "");
    if (active.length === 0) return "";
    return active.reduce((acc, [key, val]) => {
      const field = filterFields.find((f) => f.propertyName === key);
      const backendKey = field?.filterKey ?? key;
      return acc + backendKey + ":" + val + ",";
    }, "");
  }

  function buildSearchParams(searchTerm, page, filters, size) {
    let params = `?page=${page}&size=${size}`;
    const text = buildTextPart(searchTerm);
    const filter = buildFilterPart(filters);
    if (text) params += `&search=${text}`;
    if (filter) params += `&filter=${filter}`;
    return params;
  }

  function search(searchTerm, page, filters, size) {
    const searchFields = shema.fields.filter((f) => f.search);

    if (shema.searchFromServer) {
      getEntities(shema.searchRoute, buildSearchParams(searchTerm, page, filters, size)).then((response) => {
        setFilteredEntities(normalizeResponse(response));
        setTotalPages(response?.totalPages ?? null);
        setCurrentPage(response?.number ?? 0);
      });
    } else {
      const filteredItems = entities.filter((entity) => {
        for (let index = 0; index < searchFields.length; index++) {
          const f = searchFields[index];
          let val;
          if (f.isObject) val = entity[f.propertyName].name;
          else val = entity[f.propertyName];
          if (val.toLowerCase().includes(searchTerm.toLowerCase())) return true;
        }
        return false;
      });
      setFilteredEntities(filteredItems);
    }
  }

  const handlePageChange = (newPage) => search(searchText, newPage, filterSelections, pageSize);

  async function handleExport() {
    const text = buildTextPart(searchText);
    const filter = buildFilterPart(filterSelections);
    const parts = [];
    if (text) parts.push(`search=${text}`);
    if (filter) parts.push(`filter=${filter}`);
    const url = `/api/${shema.searchRoute}/export${parts.length ? "?" + parts.join("&") : ""}`;
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = `${shema.searchRoute}.xlsx`;
    a.click();
    URL.revokeObjectURL(objUrl);
  }

  function getPageNumbers(current, total) {
    const delta = 2;
    const window = [];
    for (let i = Math.max(0, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      window.push(i);
    }
    const result = [];
    if (window[0] > 0) {
      result.push(0);
      if (window[0] > 1) result.push("...");
    }
    result.push(...window);
    if (window[window.length - 1] < total - 1) {
      if (window[window.length - 1] < total - 2) result.push("...");
      result.push(total - 1);
    }
    return result;
  }

  return (
    <div className="flex-grow-1 p-4 overflow-auto">
      <div className="container mb-3">
        <div className="d-flex gap-2 align-items-center">
          {extraFilters}
          <input
            className="form-control flex-grow-1"
            type="search"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search"
            aria-label="Search"
          />
          {filterFields.map((field) => {
            const pluralLabel = field.name.endsWith("y")
              ? field.name.slice(0, -1) + "ies"
              : field.name.endsWith("s")
              ? field.name + "es"
              : field.name + "s";
            const opts = field.filterOptions
              ? field.filterOptions
              : (filterOptions[field.propertyName] ?? []).map((o) => ({ label: o.name, value: String(o.id) }));
            return (
              <FilterDropdown
                key={field.propertyName}
                style={{ minWidth: "110px", maxWidth: "180px" }}
                placeholder={"All " + pluralLabel}
                options={opts}
                value={filterSelections[field.propertyName]}
                onChange={(val) => handleFilter(field.propertyName, val)}
              />
            );
          })}
          {shema.allowExport && (
            <button className="btn btn-outline-secondary" onClick={handleExport}>
              Export
            </button>
          )}
          <button className="btn btn-primary" onClick={() => navigate("new")}>
            New
          </button>
        </div>
      </div>
      <table className="table table-hover">
        <thead>
          <tr>
            {shema.fields.filter((f) => !f.hidden).map((field, i) => {
              const sortable = !field.isArray && !field.renderCell;
              const isActive = sortConfig.field === field;
              return (
                <th
                  key={i}
                  className={sortable ? "sortable-header" : ""}
                  onClick={sortable ? () => handleSort(field) : undefined}
                >
                  {field.name}
                  {sortable && (
                    <span className="sort-icon">
                      {isActive ? (sortConfig.dir === "asc" ? " ▲" : " ▼") : " ⇅"}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedEntities.map((entity) => (
            <tr role="button" key={entity.id} onClick={() => userClick(entity.id)}>
              {shema.fields.filter((f) => !f.hidden).map((field, i) => {
                if (field.renderCell) {
                  return field.renderCell(entity, i);
                } else if (field.isArray) {
                  return (
                    <td key={i}>
                      {entity[field.propertyName].map((p, j) => (
                        <div key={j}><small>{p.name}</small></div>
                      ))}
                    </td>
                  );
                } else if (field.isObject) {
                  return <td key={i}>{entity[field.propertyName]?.name}</td>;
                } else {
                  if (isArray(field.propertyName)) {
                    return (
                      <td key={i}>
                        {field.propertyName.map((property) => entity[property] + " ")}
                      </td>
                    );
                  } else {
                    return <td key={i}>{field.format ? field.format(entity[field.propertyName]) : entity[field.propertyName]}</td>;
                  }
                }
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <nav>
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                Previous
              </button>
            </li>
            {getPageNumbers(currentPage, totalPages).map((p, i) =>
              p === "..." ? (
                <li key={`dots-${i}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li key={p} className={`page-item ${p === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(p)}>
                    {p + 1}
                  </button>
                </li>
              )
            )}
            <li className={`page-item ${currentPage === totalPages - 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

export default Entities;
